import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import {
  RotateCcw,
  ShieldCheck,
  Ruler
} from "lucide-react";
import { getRecommendedSize } from './size-guide/sizeRecommendation';
import LimitedByDesign from "./LimitedByDesign";
import { trackEvent } from "./utils/analytics";
import './ProductPreview.css';
import Cart from './Cart';
import brandLogo from "./assets/still_logo_white.png";
import cartStatic from './assets/cart.png';
import cartGif from './assets/cart.gif';
import Modal from 'react-modal';
import { useContext } from "react";
import SaleContext from "./context/SaleContext";
import { getSalePrice } from "./utils/pricing";

Modal.setAppElement('#root');

const ProductPreview = ({ products, cart, addToCart, setCartItems }) => {
  const { sale } = useContext(SaleContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [recommendedSize, setRecommendedSize] = useState(null);

  const [product, setProduct] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [mainImage, setMainImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [notification, setNotification] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [pincode, setPincode] = useState(
    localStorage.getItem("deliveryPincode") || ""
  );
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  // null | "checking" | "available" | "not_available"
  const pageRef = useRef(null);
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState({
    average: 0,
    count: 0
  });

  const recommendedProducts = React.useMemo(() => {
    if (!product || !products.length) return [];

    // Exclude:
    // 1. The current product
    // 2. All other colour/variant products with the same styleID
    const otherProducts = products.filter(
      p =>
        p._id !== product._id &&
        (!product.styleID || p.styleID !== product.styleID)
    );

    // Separate products into current collection and other collections
    const sameBrand = otherProducts.filter(
      p => p.brand === product.brand
    );

    const otherBrands = otherProducts.filter(
      p => p.brand !== product.brand
    );

    // Recommendation priority:
    // 1. Same collection + Top Pick
    // 2. Same collection
    // 3. Other collections + Top Pick
    // 4. Other collections
    const prioritized = [
      ...sameBrand.filter(p => p.topPick),
      ...sameBrand.filter(p => !p.topPick),
      ...otherBrands.filter(p => p.topPick),
      ...otherBrands.filter(p => !p.topPick),
    ];

    // Keep only ONE product per styleID
    const uniqueByStyle = [];
    const seenStyleIDs = new Set();

    for (const p of prioritized) {
      if (p.styleID) {
        // Skip duplicate colour/variant of the same style
        if (seenStyleIDs.has(p.styleID)) continue;

        seenStyleIDs.add(p.styleID);
      }

      uniqueByStyle.push(p);

      // Only show 3 recommendations
      if (uniqueByStyle.length === 3) break;
    }

    return uniqueByStyle;
  }, [product, products]);

  const styleVariants = (() => {
    if (!product) return [];

    return products.filter(
      p =>
        p.styleID &&
        product.styleID &&
        p.styleID === product.styleID
    );
  })();

  useEffect(() => {
    if (!products.length) return;

    const foundProduct = products.find(p => p._id === id);
    if (!foundProduct) {
      navigate('/not-found');
      return;
    }

    setProduct(foundProduct);
    trackEvent({
      event: "view_item",
      ecommerce: {
        currency: "INR",
        value: foundProduct.price,
        items: [
          {
            item_id: foundProduct._id,
            item_name: foundProduct.name,
            item_category: foundProduct.category,
            item_brand: foundProduct.brand,
            price: foundProduct.price
          }
        ]
      }
    });
    setMainImage(foundProduct.images?.main || '');

    const measurements = JSON.parse(localStorage.getItem("shushMeasurement"));

    if (measurements) {
      const rec = getRecommendedSize(measurements, foundProduct.sizes);
      setRecommendedSize(rec);
    } else {
      setRecommendedSize(null);
    }
  }, [id, products, navigate]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [id]);

  useEffect(() => {
    setSelectedSize('');
    setDeliveryStatus(null);
    setActiveTab(null);
  }, [id]);

  useEffect(() => {
    if (!product) return;

    const fetchReviews = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/reviews/${product._id}`);
        const data = await res.json();

        setReviews(data);

        if (data.length > 0) {
          const avg =
            data.reduce((sum, r) => sum + r.rating, 0) / data.length;

          setRatingSummary({
            average: avg,
            count: data.length
          });
        } else {
          setRatingSummary({ average: 0, count: 0 });
        }
      } catch (error) {
        console.error("Failed to fetch reviews");
      }
    };

    fetchReviews();
  }, [product]);

  // Add to cart and persist to localStorage — with notification
  const addToCartHandler = () => {
    if (!selectedSize) {
      setNotification("⚠️ Please select a size.");
      setTimeout(() => setNotification(""), 2000);
      return;
    }

    addToCart({
    ...product,
    selectedSize
  });

    // set the same notification message and clear after 2s
    setNotification("✓ Added to cart!");
    setTimeout(() => setNotification(""), 2000);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (!product) return <div>Loading...</div>;

  const salePrice = getSalePrice(product.price, sale);

  const isSaleActive =
    Boolean(sale) &&
    salePrice < product.price;

  const imageList = Object.values(product.images || {});
  const currentUrl = window.location.href;

  const checkDelivery = async () => {
    if (pincode.length !== 6) {
      setDeliveryStatus("not_available");
      return;
    }

    setDeliveryStatus("checking");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/check-pincode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pincode })
      });

      const data = await res.json();

      if (data.serviceable) {
        setDeliveryStatus("available");
        localStorage.setItem("deliveryPincode", pincode);
      } else {
        setDeliveryStatus("not_available");
      }
    } catch {
      setDeliveryStatus("not_available");
    }
  };

  const getConfidenceCopy = (confidence) => {
    switch (confidence) {
      case "High":
        return "Best fit match";
      case "Medium":
        return "Likely fit";
      case "Low":
        return "Based on your fit";
      default:
        return "";
    }
  };

  const renderStars = (rating) => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        stars.push(<FaStar key={i} />);
      } else if (rating >= i - 0.5) {
        stars.push(<FaStarHalfAlt key={i} />);
      } else {
        stars.push(<FaRegStar key={i} />);
      }
    }

    return stars;
  };

  return (
    <>
      {/* === SEO & METADATA SECTION === */}
      <Helmet>
        {/* Dynamic Title */}
        <title>{`${product.name} | STILL Footwear`}</title>

        {/* Description for Google Search */}
        <meta name="description" content={`Shop the ${product.name}. ${product.description.substring(0, 150)}...`} />

        {/* Open Graph (Facebook/WhatsApp/LinkedIn) */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} - ₹${product.price}`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.images?.main} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:price:amount" content={product.price} />
        <meta property="og:price:currency" content="INR" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product.name} />
        <meta name="twitter:image" content={product.images?.main} />

        {/* Canonical URL to avoid duplicate content penalties */}
        <link rel="canonical" href={currentUrl} />

        {/* JSON-LD Schema: The "Gold Standard" for Google Shopping */}
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "${product.name}",
            "image": "${product.images?.main}",
            "description": "${product.description}",
            "brand": {
              "@type": "Brand",
              "name": "STILL"
            },
            "offers": {
              "@type": "Offer",
              "url": "${currentUrl}",
              "priceCurrency": "INR",
              "price": "${product.price}",
              "availability": "https://schema.org/${product.sizes?.some(s => s.stock > 0) ? 'InStock' : 'OutOfStock'}"
            }
          }
        `}
        </script>
      </Helmet>

      {/* === HEADER NAV === */}
      <header>
        <div className="pp-logo">
        <Link to="/">
          <img src={brandLogo} alt="SHUSH" />
        </Link>

        </div>
        <div className="pp-navigation">
          <ul>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/about">About</a></li>
            <li><a href="/shop">Shop</a></li>
            <li className="cart-header">
              <button
                type="button"
                className="cart-button"
                onClick={openCart}
                aria-label={`Shopping cart${cartCount > 0 ? `, ${cartCount} items` : ''}`}
              >
                <div className="cart-icon">

                  <img
                    src={cartStatic}
                    alt=""
                    className="cart-static"
                  />

                  <img
                    src={cartGif}
                    alt=""
                    className="cart-gif"
                  />

                  {cartCount > 0 && (
                    <span className="cart-badge">
                      {cartCount}
                    </span>
                  )}

                </div>
              </button>
            </li>
          </ul>
        </div>
      </header>

      {/* === PRODUCT PREVIEW CONTENT === */}
      <div className="product-preview-page" ref={pageRef}>
        <main>
          <div className="preview-grid">
            {/* === IMAGE SECTION === */}
            <div className="preview-image-section">
              <div className="main-product-image-wrapper">
                <img src={mainImage} alt="Main Shoe Preview" className="main-product-image" />
              </div>

              <div className="thumbnail-carousel">
                {imageList.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`shoe angle ${idx}`}
                    className={`thumbnail ${mainImage === img ? 'active' : ''}`}
                    onClick={() => setMainImage(img)}
                  />
                ))}
              </div>
            </div>

            {/* === INFO SECTION === */}
            <div className="preview-info-section">
              <h1 className="product-title"> {product.name}</h1>
              <h1 className="collection-title">{product.brand} Collection</h1>
              {ratingSummary.count > 0 && (
                <div className="product-rating-preview">
                  <div className="stars">
                    {renderStars(ratingSummary.average)}
                  </div>
                  <span className="rating-count">
                    ({ratingSummary.count})
                  </span>
                </div>
              )}
              <div className="shop-product-price-container">
                {isSaleActive ? (
                  <>
                    <p className="shop-product-original-price">
                      ₹{product.price}
                    </p>

                    <p className="shop-product-sale-price">
                      ₹{salePrice}
                    </p>
                    <span className="shop-product-sale-badge">
                      {sale.badgeText ||
                        (sale.discountType === "percentage"
                          ? `${sale.discountValue}% OFF`
                          : `₹${sale.discountValue} OFF`)}
                    </span>
                  </>
                ) : (
                  <p className="shop-product-price">
                    ₹{product.price}
                  </p>
                )}
              </ div >
              {styleVariants.length > 1 && (
                <div className="style-variants">
                  <p className="variant-label">
                    Available Colours
                  </p>

                  <div className="variant-grid">
                    {styleVariants.map((variant) => (
                      <Link
                        key={variant._id}
                        to={`/product/${variant._id}`}
                        className={`variant-card ${
                          variant._id === product._id ? "active" : ""
                        }`}
                      >
                      <img src={variant.images?.main} alt={variant.name} />
                        <span className="variant-colour">
                          {variant.appearance?.primaryColor}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="info-tabs">
                <div className="tab-headers">
                  <button
                    className={activeTab === "description" ? "active" : ""}
                    onClick={() =>
                      setActiveTab(activeTab === "description" ? null : "description")
                    }
                  >
                    About {product.name} ▾
                  </button>
                  <button
                    className={activeTab === "details" ? "active" : ""}
                    onClick={() =>
                      setActiveTab(activeTab === "details" ? null : "details")
                    }
                  >
                    Product Details ▾
                  </button>

                  <button
                    className={activeTab === "size" ? "active" : ""}
                    onClick={() =>
                      setActiveTab(activeTab === "size" ? null : "size")
                    }
                  >
                    Size Chart▾
                  </button>
                </div>



                {activeTab && (
                  <div className="tab-content">
                    {activeTab === "details" && (
                      <div className="details-content">
                        {product.materials && (
                          <ul>
                            <li><strong>Upper:</strong> {product.materials.upper}</li>
                            <li><strong>Inner Lining:</strong> {product.materials.lining}</li>
                            <li><strong>Insole:</strong> {product.materials.insole}</li>
                            <li><strong>Sole:</strong> {product.materials.sole}</li>
                          </ul>
                        )}

                        {product.appearance && (
                          <ul>
                            <li><strong>Colour:</strong> {product.appearance.primaryColor}</li>
                            {product.appearance.finish && (
                              <li><strong>Finish:</strong> {product.appearance.finish}</li>
                            )}
                          </ul>
                        )}

                        {product.fit?.hasHeel && (
                          <ul>
                            <li><strong>Heel Height:</strong> {product.fit.heelHeight} inches</li>
                          </ul>
                        )}
                      </div>
                    )}

                    {activeTab === "size" && (
                      <div className="size-content">
                        <table>
                          <thead>
                            <tr>
                              <th>EU</th>
                              <th>Shoe length (cm)</th>
                              <th>Recommended foot length (cm)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.sizes.map(s => (
                              <tr key={s.size}>
                                <td>{s.size}</td>
                                <td>{s.length.toFixed(1)}</td>
                                <td>{(s.length - 1).toFixed(1)} – {(s.length - 0.5).toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <p className="size-note">
                          Measurements include recommended toe space.
                        </p>
                      </div>
                    )}

                    {activeTab === "description" && (
                      <div className="product-description">
                        <p> {product.description}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Recommended size box */}
              <div className="recommended-size-box">
                {!recommendedSize ? (
                  <>
                    <span>Prefer a guided recommendation?</span>
                    <Link to="/size-guide" className="size-guide-link">
                      Check your size →
                    </Link>
                  </>
                ) : recommendedSize.message === "Size not available" ? (
                  <span className="size-unavailable">Size not available</span>
                ) : (
                  <>
                    <span>Recommended STILL Size :</span>
                    <strong>{recommendedSize.bestFit}</strong>
                    <span className={`fit-confidence ${recommendedSize.confidence.toLowerCase()}`}>
                      {getConfidenceCopy(recommendedSize.confidence)}
                    </span>
                    <Link to="/size-guide" className="size-guide-link">
                      Recheck your fit →
                    </Link>
                  </>
                )}
              </div>

              <div className="purchase-section">
                <div className="size-selector">
                  <label>Select your size:</label>

                  <div className="size-grid">
                    {product?.sizes?.map((sizeObj) => (
                      <div
                        key={sizeObj.size}
                        className="size-option-wrapper"
                      >
                        <button
                          type="button"
                          className={`size-box
                            ${selectedSize === sizeObj.size ? "selected" : ""}
                            ${sizeObj.stock <= 0 ? "out-of-stock" : ""}
                          `}
                          disabled={sizeObj.stock <= 0}
                          onClick={() => setSelectedSize(sizeObj.size)}
                        >
                          {sizeObj.size}
                        </button>

                        {sizeObj.stock === 1 && (
                          <span className="size-scarcity">
                            Last Pair
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {recommendedSize &&
                    selectedSize &&
                    selectedSize !== recommendedSize.bestFit && (
                      <p className="fit-hint">
                        You're selecting a different size than our recommendation.
                      </p>
                  )}
                </div>

                <button
                  className="add-to-cart-button"
                  onClick={addToCartHandler}
                  disabled={deliveryStatus === "not_available"}
                >
                  Add to Cart
                </button>
              </div>

              <LimitedByDesign />

              <div className="mobile-purchase-bar">
                <div className="mobile-purchase-info">
                {ratingSummary.count > 0 && (
                  <div className="product-rating-preview">
                    <div className="stars">
                      {renderStars(ratingSummary.average)}
                    </div>
                    <span className="rating-count">
                      ({ratingSummary.count})
                    </span>
                  </div>
                )}
                  <span>
                    {selectedSize ? `Size ${selectedSize}` : "Select Size"}
                  </span>

                  {isSaleActive ? (
                    <div className="mobile-price">
                      <span className="mobile-sale-price">
                        ₹{salePrice}
                      </span>

                      <span className="mobile-original-price">
                        ₹{product.price}
                      </span>
                      <span className="mobile-sale-badge">
                        {sale.badgeText}
                      </span>
                    </div>
                  ) : (
                    <strong>₹{product.price}</strong>
                  )}
                </div>

                <button
                  className="mobile-add-to-cart"
                  onClick={addToCartHandler}
                  disabled={deliveryStatus === "not_available"}
                >
                  Add to Cart
                </button>
              </div>

              <div className="trust-strip">
                <div className="trust-item">
                  <RotateCcw size={18} />
                  <span>Easy Returns</span>
                </div>

                <div className="trust-item">
                  <ShieldCheck size={18} />
                  <span>Secure Razorpay Payment</span>
                </div>

                <div className="trust-item">
                  <Ruler size={18} />
                  <span> Guided Recommendation</span>
                </div>

              </div>

              <div className="delivery-check-box">
                <label>Check delivery availability</label>

                <div className="delivery-input-row">
                  <input
                    type="text"
                    placeholder="Enter Pincode"
                    value={pincode}
                    maxLength={6}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                  />
                  <button onClick={checkDelivery}>
                    Check
                  </button>
                </div>

                {deliveryStatus === "checking" && (
                  <p className="delivery-info">Checking delivery availability…</p>
                )}

                {deliveryStatus === "available" && (
                  <p className="delivery-success">
                    ✓ Delivery available to this location
                  </p>
                )}

                {deliveryStatus === "not_available" && (
                  <p className="delivery-error">
                    Sorry, this area is not serviceable by us right now, but please check back with us!
                  </p>
                )}
              </div>


              <div className="product-meta">
                <a href="/return-policy" className="meta-link" target="_blank" rel="noopener noreferrer">Return & Refund Policy</a>
              </div>
              {/* === DISCLAIMER ACCORDION === */}
              <div className="disclaimer-section">
                <button
                  className="disclaimer-toggle"
                  onClick={() => setShowDisclaimer(prev => !prev)}
                >
                  Disclaimer {showDisclaimer ? "−" : "+"}
                </button>

                {showDisclaimer && (
                  <p className="disclaimer-text">
                    Every pair is photographed in studio lighting to reflect true detail.
                    Subtle variations in color may occur across screens and lighting environments.
                  </p>
                )}
              </div>

            </div>
          </div>

          {/* === REVIEWS SECTION === */}
          <div className="reviews-section">
            <h2>STILL Customers Say</h2>

            {ratingSummary.count > 0 && (
              <div className="product-rating-preview">
                <div className="stars">
                  {renderStars(ratingSummary.average)}
                </div>
                <p>
                  {ratingSummary.average.toFixed(1)} out of 5 ({ratingSummary.count} reviews)
                </p>
              </div>
            )}

            <p className="reviews-trust">
              Verified buyers share their fit and comfort experience.
            </p>

            {reviews.length === 0 && (
              <p className="no-reviews">No reviews yet.</p>
            )}

            <div className="reviews-list">
              {reviews.map((review) => (
                <div key={review._id} className="review-card">
                  <div className="review-card-top">
                    <span className="review-name">{review.userName}</span>
                    <span className="review-stars">
                      {renderStars(review.rating)}
                    </span>
                  </div>

                  {review.verifiedPurchase && (
                    <span className="verified-badge">Verified Purchase</span>
                  )}

                  {review.fit && (
                    <span className="review-fit-badge">{review.fit}</span>
                  )}

                  {review.comment && (
                    <p className="review-comment">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* === RECOMMENDATIONS === */}
          {recommendedProducts.length > 0 && (
              <div className="recommendations-section">
                <h2>You May Also Admire</h2>
                <div className="recommendation-carousel">
                {recommendedProducts.map((p) => (
                  <Link
                    to={`/product/${p._id}`}
                    className="recommendation-card group" // Add 'group' for hover targeting
                    key={p._id}
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3">
                      <img src={p.images?.main} alt={p.name} className="w-full h-[240px] object-cover transition-transform duration-500 group-hover:scale-105" />
                      {/* Subtle hover overlay cue */}
                      <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-serif text-[#4b2326]">{p.name}</p>
                        <p className="text-sm text-gray-500 mt-1">₹{p.price}</p>
                      </div>
                      {/* Assuming you pass styleVariants logic down, or just show a + icon */}
                      <span className="text-xs border border-[#4b2326] text-[#4b2326] px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        View
                      </span>
                    </div>
                  </Link>
                  ))}
                </div>
              </div>
            )}

        </main>
      </div>

      {/* Notification (same as Product component) */}
      {notification && (
        <div className="notification">
          {notification}
        </div>
      )}

      {/* === CART MODAL === */}
      <Modal
        isOpen={isCartOpen}
        onRequestClose={closeCart}
        contentLabel="Shopping Cart"
        className="cart-modal"
        overlayClassName="cart-overlay"
      >
        <button onClick={closeCart} className="close-button">Close</button>
        <Cart cart={cart} setCartItems={setCartItems} />
      </Modal>

      {/* === FOOTER === */}
      <footer>
        <p>&copy;2025 SHUSH Footwear LLP. Trading as STILL™. All rights reserved.</p>
        <div className="footer-links">
          <a href="/privacy-policy" className="meta-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span aria-hidden="true"> | </span>
          <a href="/terms-and-conditions-policy" className="meta-link" target="_blank" rel="noopener noreferrer">Terms Of Service</a>
        </div>
      </footer>
    </>
  );
};

export default ProductPreview;
